const FEED_LIMIT = 3 * 1024 * 1024;
const FEED_HOSTS = new Set(['api.mixwave.com', 'mixwave-website-downloads-preview.mixwave.workers.dev']);
const LOOPBACK_HOSTS = new Set(['127.0.0.1', 'localhost', '[::1]']);

export function releaseEndpoint(value, location) {
  if (typeof value !== 'string' || !value.trim() || /[\\\u0000-\u001f]/.test(value)) throw new Error('Missing release endpoint');
  const base = new URL(typeof location === 'string' ? location : location.href);
  const url = new URL(value, base);
  const local = LOOPBACK_HOSTS.has(base.hostname) && url.origin === base.origin && ['http:', 'https:'].includes(url.protocol);
  const hosted = url.protocol === 'https:' && !url.port && FEED_HOSTS.has(url.hostname);
  if ((!local && !hosted) || url.username || url.password || url.pathname !== '/website/downloads' || url.search || url.hash) {
    throw new Error('Invalid release endpoint');
  }
  return url.href;
}

export function resolveProductRelease(feed, { productHandle, trialHandle, trialMode = 'catalog', now = new Date() } = {}, helpers) {
  if (!helpers || !['validateDownloadsFeed', 'isRecentUpdate', 'safeURL'].every(key => typeof helpers[key] === 'function')) {
    throw new Error('Release helpers are unavailable');
  }
  const { products } = helpers.validateDownloadsFeed(feed);
  const release = products.find(product => product.productHandle === productHandle);
  const trial = trialMode === 'catalog' && trialHandle ? products.find(product => product.productHandle === trialHandle) : null;
  const eligible = trial && !trial.isCollection && ['plugin', 'drum-library'].includes(trial.productType);
  return {
    updateAvailable: !!release && helpers.isRecentUpdate(release.updatedDate, now, release.releaseDate),
    mac: eligible ? helpers.safeURL(trial.macInstaller, 'installer') : '',
    win: eligible ? helpers.safeURL(trial.pcInstaller, 'installer') : '',
  };
}

async function readFeed(response) {
  if (!response?.ok) throw new Error('Release lookup failed');
  const advertised = Number(response.headers?.get('content-length'));
  if (Number.isFinite(advertised) && advertised > FEED_LIMIT) throw new Error('Release response is too large');
  let source = '';
  if (response.body?.getReader) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let size = 0;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > FEED_LIMIT) throw new Error('Release response is too large');
        source += decoder.decode(value, { stream: true });
      }
      source += decoder.decode();
    } catch (error) {
      await reader.cancel().catch(() => {});
      throw error;
    } finally { reader.releaseLock(); }
  } else {
    source = await response.text();
    if (new TextEncoder().encode(source).byteLength > FEED_LIMIT) throw new Error('Release response is too large');
  }
  return JSON.parse(source);
}

export function createProductRelease(config, options = {}) {
  const doc = config.ownerDocument;
  const view = doc.defaultView;
  const scope = config.closest('.shopify-section') || config.parentElement;
  const find = selector => scope.querySelector(selector);
  const pills = find('.product-hero__pills-row');
  const status = find('[data-trial-download-status]');
  const message = find('[data-trial-download-message]');
  const retry = find('[data-trial-download-retry]');
  const buttons = find('.trial-popup__download-buttons');
  const links = { mac: find('[data-trial-download-platform="mac"]'), win: find('[data-trial-download-platform="win"]') };
  const fetcher = options.fetch || view.fetch.bind(view);
  const location = options.location || view.location;
  const loadCatalog = options.loadCatalog || (url => import(url));
  const now = options.now || (() => new Date());
  const timerSet = options.setTimeout || view.setTimeout.bind(view);
  const timerClear = options.clearTimeout || view.clearTimeout.bind(view);
  let state = 'idle', destroyed = false, generation = 0, pending, controller;
  const mode = config.dataset.trialMode;
  const catalogMode = mode === 'catalog';

  const clearPill = () => {
    for (const pill of scope.querySelectorAll('.product-hero__tag-pill--update')) pill.remove();
  };
  const setDownloads = ({ mac = '', win = '' } = {}) => {
    if (!catalogMode) return;
    let count = 0;
    for (const [platform, link] of Object.entries(links)) {
      if (!link) continue;
      const url = platform === 'mac' ? mac : win;
      link.hidden = !url;
      if (url) {
        link.setAttribute('href', url);
        link.removeAttribute('aria-disabled');
        count++;
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
      }
    }
    buttons?.classList.toggle('has-multiple', count > 1);
  };
  const setStatus = (text = '', canRetry = false) => {
    if (!catalogMode) return;
    if (message) message.textContent = text;
    if (status) status.hidden = !text;
    if (retry) retry.hidden = !canRetry;
    buttons?.setAttribute('aria-busy', String(state === 'loading'));
  };

  function load() {
    if (destroyed) return Promise.resolve();
    if (pending) return pending;
    const current = ++generation;
    const request = new AbortController();
    controller = request;
    state = 'loading';
    clearPill();
    setDownloads();
    setStatus('Loading downloads…');
    const timer = timerSet(() => request.abort(), options.timeoutMs ?? 20_000);
    let removeAbort;
    const aborted = new Promise((resolve, reject) => {
      const onAbort = () => reject(new Error('Release lookup was interrupted'));
      request.signal.addEventListener('abort', onAbort, { once: true });
      removeAbort = () => request.signal.removeEventListener('abort', onAbort);
    });
    const work = async () => {
      if (!['catalog', 'manual', 'downloads'].includes(mode)) throw new Error('Invalid trial source');
      const endpoint = releaseEndpoint(config.dataset.endpoint, location);
      if (!config.dataset.catalogModule) throw new Error('Missing release module');
      const helpers = await loadCatalog(config.dataset.catalogModule);
      if (request.signal.aborted) throw new Error('Release lookup was interrupted');
      const response = await fetcher(endpoint, { credentials: 'omit', cache: 'no-store', redirect: 'error', signal: request.signal });
      const feed = await readFeed(response);
      return resolveProductRelease(feed, { productHandle: config.dataset.productHandle,
        trialHandle: config.dataset.trialHandle, trialMode: mode, now: now() }, helpers);
    };
    pending = Promise.race([work(), aborted]).then(result => {
      if (destroyed || current !== generation) return;
      state = 'ready';
      if (result.updateAvailable && pills) {
        const pill = doc.createElement('div');
        pill.className = 'product-hero__tag-pill product-hero__tag-pill--red product-hero__tag-pill--update';
        pill.textContent = 'Update Available';
        pills.appendChild(pill);
      }
      setDownloads(result);
      setStatus(result.mac || result.win ? '' : 'Downloads aren’t available here yet.');
    }).catch(() => {
      if (destroyed || current !== generation) return;
      state = 'failed';
      clearPill();
      setDownloads();
      setStatus('Downloads are temporarily unavailable. Please try again.', true);
    }).finally(() => {
      timerClear(timer);
      removeAbort();
      if (current === generation) { pending = undefined; controller = undefined; }
    });
    return pending;
  }
  const retryFailed = () => { if (state === 'failed') void load(); };
  const onRetry = event => { event.preventDefault(); retryFailed(); };
  retry?.addEventListener('click', onRetry);
  scope.addEventListener('mw:trial-open', retryFailed);
  scope.addEventListener('mw:trial-downloads-ready', retryFailed);
  return {
    ready: load(), load,
    reload() {
      generation++;
      controller?.abort();
      pending = undefined;
      return load();
    },
    destroy() {
      destroyed = true;
      generation++;
      controller?.abort();
      retry?.removeEventListener('click', onRetry);
      scope.removeEventListener('mw:trial-open', retryFailed);
      scope.removeEventListener('mw:trial-downloads-ready', retryFailed);
    },
  };
}

const mounted = new WeakMap();
function configsWithin(scope) {
  const configs = [...scope.querySelectorAll('[data-mw-hero-releases]')];
  if (scope.matches?.('[data-mw-hero-releases]')) configs.unshift(scope);
  return configs;
}
export function mountProductReleases(scope = document, options = {}) {
  return configsWithin(scope).map(config => {
    if (!mounted.has(config)) mounted.set(config, createProductRelease(config, options));
    return mounted.get(config);
  });
}
export function unmountProductReleases(scope) {
  for (const config of configsWithin(scope)) {
    mounted.get(config)?.destroy();
    mounted.delete(config);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountProductReleases(), { once: true });
  else mountProductReleases();
  document.addEventListener('shopify:section:load', event => mountProductReleases(event.target));
  document.addEventListener('shopify:section:unload', event => unmountProductReleases(event.target));
}
